import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateLong } from "@/lib/format";
import { SWAP_STATUS } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Repeat, ArrowRight, Loader2, Check, X } from "lucide-react";

function SwapCard({ swap, me, onAction }) {
  const st = SWAP_STATUS[swap.status] || { label: swap.status, cls: "bg-slate-100 text-slate-600" };
  const incoming = swap.to_user_id === me && swap.status === "requested";
  const outgoing = swap.from_user_id === me && swap.status === "requested";
  return (
    <Card className="p-4" data-testid={`swap-${swap.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{swap.from_name}</span>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">{swap.to_name}</span>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
      </div>
      {swap.service && (
        <p className="text-sm text-muted-foreground mt-2">
          {swap.service.name} · {swap.service_type_name || swap.assignment?.service_type_name} · {fmtDateLong(swap.service.date)} · {swap.service.start_time}–{swap.service.end_time}
        </p>
      )}
      {swap.message && <p className="text-sm italic mt-1">"{swap.message}"</p>}
      {(incoming || outgoing) && (
        <div className="flex gap-2 mt-3">
          {incoming && (
            <>
              <Button size="sm" onClick={() => onAction(swap.id, "accept")} data-testid={`swap-accept-${swap.id}`}>
                <Check className="w-4 h-4 mr-1" /> Accepteren
              </Button>
              <Button size="sm" variant="outline" onClick={() => onAction(swap.id, "reject")} data-testid={`swap-reject-${swap.id}`}>
                <X className="w-4 h-4 mr-1" /> Weigeren
              </Button>
            </>
          )}
          {outgoing && (
            <Button size="sm" variant="outline" onClick={() => onAction(swap.id, "withdraw")} data-testid={`swap-withdraw-${swap.id}`}>
              Intrekken
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Swaps() {
  const { user } = useAuth();
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swaps/mine");
      setSwaps(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const action = async (id, act) => {
    try {
      await api.post(`/swaps/${id}/${act}`);
      toast.success("Bijgewerkt");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const incoming = swaps.filter((s) => s.to_user_id === user?.id && s.status === "requested");
  const rest = swaps.filter((s) => !(s.to_user_id === user?.id && s.status === "requested"));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="swaps-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Ruilverzoeken</h1>
        <p className="text-muted-foreground mt-1">Verzoeken die je hebt ontvangen of verstuurd.</p>
      </div>

      {swaps.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">
          <Repeat className="w-10 h-10 mx-auto mb-3 opacity-40" /> Geen ruilverzoeken.
        </Card>
      ) : (
        <>
          {incoming.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-heading font-semibold">Actie vereist</h3>
              {incoming.map((s) => <SwapCard key={s.id} swap={s} me={user.id} onAction={action} />)}
            </div>
          )}
          <div className="space-y-2">
            <h3 className="font-heading font-semibold">Geschiedenis</h3>
            {rest.map((s) => <SwapCard key={s.id} swap={s} me={user.id} onAction={action} />)}
          </div>
        </>
      )}
    </div>
  );
}
